import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Plus, Send, UserPlus } from "lucide-react";
import { api } from "../../lib/api";
import { fmtDateTime } from "../../lib/format";

const inputCls = "w-full rounded-xl border border-[#27272A] bg-[#0A0D10] px-3.5 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#E50914] transition-colors";
const FIELD_TYPES = [["text", "Text"], ["number", "Number"], ["dropdown", "Dropdown"], ["yesno", "Yes / No"]];

// Owner-side property security console: grant/revoke VIEW/WRITE access (audited,
// property-scoped, effective immediately), manage custom security fields, and
// reply to security-user chat channels. Never exposes bookings/pricing/payouts.
export default function SecurityAccessSection({ propertyId }) {
  const [access, setAccess] = useState([]);
  const [fields, setFields] = useState([]);
  const [threads, setThreads] = useState([]);
  const [openThread, setOpenThread] = useState(null);
  const [threadMsgs, setThreadMsgs] = useState([]);
  const [reply, setReply] = useState("");
  const [email, setEmail] = useState("");
  const [perm, setPerm] = useState("view");
  const [field, setField] = useState({ name: "", field_type: "text", options: "", required: false });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, f, t] = await Promise.all([
        api.securityAccessList(propertyId), api.securityFieldsOwner(propertyId), api.securityChatThreads(propertyId)]);
      setAccess(a); setFields(f); setThreads(t);
    } catch { /* owner-only surface; parent page already guards */ }
  }, [propertyId]);
  useEffect(() => { load(); }, [load]);

  const grant = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.securityAccessGrant(propertyId, { email, permission: perm });
      toast.success(`${perm.toUpperCase()} access granted to ${email}`);
      setEmail("");
      await load();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const revoke = async (g) => {
    if (!window.confirm(`Revoke ${g.permission.toUpperCase()} access for ${g.email}? This takes effect immediately.`)) return;
    try {
      await api.securityAccessRevoke(propertyId, g.access_id);
      toast.success("Access revoked — effective immediately.");
      await load();
    } catch (err) { toast.error(err.message); }
  };

  const addField = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.securityFieldCreate(propertyId, {
        name: field.name, field_type: field.field_type, required: field.required,
        options: field.field_type === "dropdown" ? field.options.split(",").map((o) => o.trim()).filter(Boolean) : [],
      });
      toast.success("Security field added");
      setField({ name: "", field_type: "text", options: "", required: false });
      await load();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const toggleField = async (f) => {
    try {
      await api.securityFieldPatch(propertyId, f.field_id, { active: !f.active });
      await load();
    } catch (err) { toast.error(err.message); }
  };

  const openChat = async (t) => {
    setOpenThread(t.security_user_id);
    try { setThreadMsgs(await api.securityChatOwnerRead(propertyId, t.security_user_id)); }
    catch (err) { toast.error(err.message); }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    try {
      await api.securityChatOwnerSend(propertyId, openThread, reply.trim());
      setReply("");
      setThreadMsgs(await api.securityChatOwnerRead(propertyId, openThread));
    } catch (err) { toast.error(err.message); }
  };

  return (
    <section className="card-dark p-5 sm:p-6" data-testid="security-access-section">
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 rounded-xl bg-[#E50914]/15 flex items-center justify-center shrink-0">
          <ShieldCheck className="w-4 h-4 text-[#FF3B45]" />
        </span>
        <div>
          <h2 className="font-display text-lg font-bold text-white">Property Security Access</h2>
          <p className="text-xs text-zinc-500">Grant trusted users a live operations view of this property. Property-scoped, revocable any time.</p>
        </div>
      </div>

      {/* Grant form */}
      <form onSubmit={grant} className="mt-5 flex flex-col sm:flex-row gap-2.5" data-testid="security-grant-form">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="security@company.com"
               data-testid="security-grant-email" className={`${inputCls} flex-1`} />
        <select value={perm} onChange={(e) => setPerm(e.target.value)} data-testid="security-grant-permission" className={`${inputCls} sm:w-36`}>
          <option value="view">VIEW</option>
          <option value="write">WRITE</option>
        </select>
        <button type="submit" disabled={busy} className="btn-primary text-sm !py-2.5" data-testid="security-grant-submit">
          <UserPlus className="w-4 h-4" /> Grant Access
        </button>
      </form>
      <p className="mt-2 text-[11px] text-zinc-600">VIEW sees the live board, reports and chat. WRITE can additionally file reports and notes. Neither can touch bookings, pricing or payouts.</p>

      {/* Access table */}
      <div className="mt-5 overflow-x-auto" data-testid="security-access-table">
        {access.length === 0 ? (
          <p className="text-sm text-zinc-500 py-3" data-testid="security-access-empty">No security users yet.</p>
        ) : (
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-[#27272A]">
                <th className="py-2 pr-3">User</th><th className="py-2 pr-3">Email</th><th className="py-2 pr-3">Permission</th>
                <th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Last active</th><th className="py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {access.map((g) => (
                <tr key={g.access_id} className="border-b border-[#18181B]" data-testid={`security-access-row-${g.access_id}`}>
                  <td className="py-2.5 pr-3 text-zinc-200">{g.user_name || "—"}</td>
                  <td className="py-2.5 pr-3 text-zinc-400">{g.email}</td>
                  <td className="py-2.5 pr-3">
                    <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border ${g.permission === "write" ? "text-amber-300 border-amber-500/30 bg-amber-500/10" : "text-zinc-300 border-[#27272A] bg-white/5"}`}
                          data-testid={`security-perm-${g.access_id}`}>{g.permission}</span>
                  </td>
                  <td className={`py-2.5 pr-3 text-xs ${g.status === "active" ? "text-emerald-400" : "text-zinc-500"}`} data-testid={`security-status-${g.access_id}`}>{g.status}</td>
                  <td className="py-2.5 pr-3 text-xs text-zinc-500">{g.last_active_at ? fmtDateTime(g.last_active_at) : "never"}</td>
                  <td className="py-2.5 text-right">
                    {g.status === "active" && (
                      <button onClick={() => revoke(g)} className="text-xs font-semibold text-[#FF6B6B] hover:text-[#FF3B45] transition-colors"
                              data-testid={`security-revoke-${g.access_id}`}>Revoke</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Custom security fields */}
      <div className="mt-8 pt-6 border-t border-[#27272A]" data-testid="security-fields-manager">
        <p className="meta-label">Custom security fields</p>
        <p className="mt-1 text-xs text-zinc-500">Extra fields security users fill in on reports (e.g. Guard Name, Patrol Status, Incident Severity).</p>
        {fields.length > 0 && (
          <ul className="mt-3 space-y-2" data-testid="security-fields-list">
            {fields.map((f) => (
              <li key={f.field_id} className="flex items-center justify-between gap-3 text-sm" data-testid={`security-field-${f.field_id}`}>
                <span className="text-zinc-300">{f.name} <span className="text-zinc-600 text-xs">· {f.field_type}{f.required ? " · required" : ""}{f.options?.length ? ` · ${f.options.join("/")}` : ""}</span></span>
                <button onClick={() => toggleField(f)} className={`text-xs font-semibold ${f.active ? "text-amber-300" : "text-emerald-400"}`}
                        data-testid={`security-field-toggle-${f.field_id}`}>{f.active ? "Deactivate" : "Activate"}</button>
              </li>
            ))}
          </ul>
        )}
        <form onSubmit={addField} className="mt-4 grid sm:grid-cols-[1fr_140px_auto] gap-2.5 items-end" data-testid="security-field-form">
          <div>
            <label className="text-xs text-zinc-500 block mb-1.5">Field name</label>
            <input value={field.name} onChange={(e) => setField({ ...field, name: e.target.value })} required data-testid="security-field-name" className={inputCls} placeholder="Incident Severity" />
          </div>
          <div>
            <label className="text-xs text-zinc-500 block mb-1.5">Type</label>
            <select value={field.field_type} onChange={(e) => setField({ ...field, field_type: e.target.value })} data-testid="security-field-type" className={inputCls}>
              {FIELD_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <button type="submit" disabled={busy} className="btn-secondary text-sm !py-2.5" data-testid="security-field-add"><Plus className="w-4 h-4" /> Add</button>
          {field.field_type === "dropdown" && (
            <div className="sm:col-span-2">
              <label className="text-xs text-zinc-500 block mb-1.5">Options (comma separated)</label>
              <input value={field.options} onChange={(e) => setField({ ...field, options: e.target.value })} data-testid="security-field-options" className={inputCls} placeholder="Low, Medium, High" />
            </div>
          )}
          <label className="sm:col-span-3 flex items-center gap-2 text-xs text-zinc-400">
            <input type="checkbox" checked={field.required} onChange={(e) => setField({ ...field, required: e.target.checked })} data-testid="security-field-required" className="accent-[#E50914]" />
            Required on every report
          </label>
        </form>
      </div>

      {/* Security chat threads */}
      <div className="mt-8 pt-6 border-t border-[#27272A]" data-testid="security-threads">
        <p className="meta-label">Security channels</p>
        {threads.length === 0 ? (
          <p className="mt-3 text-sm text-zinc-500" data-testid="security-threads-empty">No messages from security users yet.</p>
        ) : (
          <div className="mt-3 space-y-2">
            {threads.map((t) => (
              <div key={t.channel} className="rounded-xl border border-[#27272A] bg-[#0A0D10]" data-testid={`security-thread-${t.security_user_id}`}>
                <button onClick={() => (openThread === t.security_user_id ? setOpenThread(null) : openChat(t))}
                        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left" data-testid={`security-thread-open-${t.security_user_id}`}>
                  <span className="text-sm text-zinc-200">{t.security_user_name || t.email} <span className={`text-[10px] uppercase ${t.grant_status === "active" ? "text-emerald-400" : "text-zinc-500"}`}>· {t.grant_status}</span></span>
                  <span className="text-xs text-zinc-500 truncate max-w-[45%]">{t.last_message}</span>
                </button>
                {openThread === t.security_user_id && (
                  <div className="px-4 pb-4 border-t border-[#18181B]" data-testid="security-thread-messages">
                    <div className="pt-3 space-y-2 max-h-64 overflow-y-auto">
                      {threadMsgs.map((m) => (
                        <p key={m.message_id} className={`text-sm rounded-lg px-3 py-2 max-w-[85%] ${m.sender_role === "host" ? "bg-[#E50914]/15 text-zinc-100 ml-auto" : "bg-white/5 text-zinc-300"}`}>
                          {m.text}
                        </p>
                      ))}
                    </div>
                    <form onSubmit={sendReply} className="mt-3 flex gap-2">
                      <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply…" data-testid="security-thread-reply" className={`${inputCls} flex-1 !py-2`} />
                      <button type="submit" className="btn-primary !px-3 !py-2" data-testid="security-thread-send" aria-label="Send"><Send className="w-4 h-4" /></button>
                    </form>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
